import { loadStyle } from './lazy-vendor.js';

/**
 * Cookie lišta – orestbida/cookieconsent v3.
 *
 * Knihovna je globál z vendor bundle (js/vendor/cookieconsent/cookieconsent.umd.js).
 * Konfigurace je záměrně na jednom místě pro celý web – stačí ji přidat do
 * šablony a lišta se chová všude stejně.
 *
 * KATEGORIE – stejný režim souhlasu jako ekominik.cz (kominik.local). Každá
 * volitelná kategorie má v nastavení vlastní přepínač; měřicí kódy z CMS
 * (Analytics::head) se spustí až po souhlasu s jejich kategorií. Stránka
 * /prohlaseni-o-cookies popisuje totéž – při změně upravit obojí.
 *
 *   necessary       – relace cms.local (cms_sess), volba cookies, reCAPTCHA
 *   functional      – zapamatované volby na webu
 *   analytics       – Google Analytics 4
 *   marketing       – reklama a remarketing (kódy z CMS, pokud se nastaví)
 *   personalization – přizpůsobení obsahu
 *   social          – vložený obsah (videa, mapy, sociální sítě)
 *
 * JAK PŘIDAT SKRIPT do kategorie (spustí se až po souhlasu):
 *   <script type="text/plain" data-category="analytics" data-service="Google Analytics 4">
 *       …kód měřáku…
 *   </script>
 *
 * Přístupnost: knihovna sama řeší roli dialogu, fokus i popisky. Tlačítko
 * „Nastavení cookies" v patičce otevírá okno přes atribut data-cc, takže
 * nepotřebuje vlastní JS.
 */
async function initCookieConsent() {
    if (typeof CookieConsent === 'undefined') return;

    // Vzhled lišty je v samostatném CSS – v hlavním by blokoval první vykreslení
    // každé stránky, i když se lišta ukáže jen při první návštěvě.
    await loadStyle('cookies');

    CookieConsent.run({
        // souhlas platí rok, pak se lišta zeptá znovu
        cookie: {
            name: 'kk_kolacek_consent',
            expiresAfterDays: 365,
        },

        guiOptions: {
            consentModal: {
                layout: 'cloud',
                position: 'bottom center',
                equalWeightButtons: false, // „Jen nutné" nesmí být schované, ale ani stejně výrazné
                flipButtons: false,
            },
            preferencesModal: {
                layout: 'box',
                equalWeightButtons: true,
            },
        },

        categories: {
            // reCAPTCHA je tu schválně: bez ní by šel formulář zneužít roboty,
            // takže ji řadíme mezi nutné (ochrana služby, kterou návštěvník sám
            // vyvolal).
            necessary: {
                enabled: true,
                readOnly: true,
                services: {
                    session: { label: 'Relace redakčního systému (cms_sess)' },
                    recaptcha: { label: 'Google reCAPTCHA (ochrana formuláře proti robotům)' },
                },
            },

            functional: {
                services: {
                    preferences: { label: 'Zapamatované volby na webu' },
                },
            },

            analytics: {
                autoClear: {
                    cookies: [
                        { name: /^_ga/ },
                    ],
                },
                services: {
                    ga4: { label: 'Google Analytics 4' },
                },
            },

            marketing: {
                autoClear: {
                    cookies: [
                        { name: /^_gcl/ },
                        { name: /^_fbp/ },
                    ],
                },
                services: {
                    ads: { label: 'Reklama a remarketing' },
                },
            },

            personalization: {
                services: {
                    content: { label: 'Přizpůsobení obsahu' },
                },
            },

            social: {
                services: {
                    embeds: { label: 'Videa, mapy a příspěvky vložené do stránky' },
                },
            },
        },

        language: {
            default: 'cs',
            translations: {
                cs: {
                    consentModal: {
                        title: 'Na webu používáme cookies',
                        // Krátce schválně: dlouhý odstavec lišty byl na mobilu největší
                        // text v záběru, PageSpeed ho bral jako LCP a čekal na JS.
                        description:
                            'Nutné cookies potřebujeme, aby web fungoval. Ostatní zapneme jen s vaším souhlasem. <a href="/prohlaseni-o-cookies">Více o cookies</a>',
                        acceptAllBtn: 'Přijmout vše',
                        acceptNecessaryBtn: 'Jen nutné',
                        showPreferencesBtn: 'Nastavení',
                    },
                    preferencesModal: {
                        title: 'Nastavení cookies',
                        acceptAllBtn: 'Přijmout vše',
                        acceptNecessaryBtn: 'Jen nutné',
                        savePreferencesBtn: 'Uložit nastavení',
                        closeIconLabel: 'Zavřít',
                        serviceCounterLabel: 'služba|služby',
                        sections: [
                            {
                                title: 'Jak cookies používáme',
                                description:
                                    'Cookies jsou malé soubory, které si web ukládá do prohlížeče. Níže si můžete zapnout jen ty kategorie, se kterými souhlasíte. Volbu jde kdykoli změnit odkazem „Nastavení cookies" v patičce.',
                            },
                            {
                                title: 'Nutné cookies <span class="pm__badge">Vždy zapnuté</span>',
                                description:
                                    'Bez nich web nefunguje – drží relaci redakčního systému, pamatují si vaši volbu cookies a chrání poptávkový formulář před roboty (Google reCAPTCHA). Nejdou vypnout.',
                                linkedCategory: 'necessary',
                            },
                            {
                                title: 'Funkční cookies',
                                description:
                                    'Pamatují si vaše volby na webu. Bez nich web funguje, jen méně pohodlně.',
                                linkedCategory: 'functional',
                            },
                            {
                                title: 'Analytické cookies',
                                description:
                                    'Google Analytics 4 nám ukazuje, které stránky a služby lidé čtou a odkud přišli. Výsledky vidíme jen v souhrnu, ne po jednotlivých návštěvnících.',
                                linkedCategory: 'analytics',
                            },
                            {
                                title: 'Marketingové cookies',
                                description:
                                    'Umožňují ukázat naši nabídku lidem, kteří web už navštívili, a měřit, jestli má reklama smysl. Bez souhlasu se nespustí.',
                                linkedCategory: 'marketing',
                            },
                            {
                                title: 'Personalizace obsahu',
                                description:
                                    'Přizpůsobuje, co na webu uvidíte – například jaké služby nebo realizace vám nabídneme jako první.',
                                linkedCategory: 'personalization',
                            },
                            {
                                title: 'Sociální sítě a vložený obsah',
                                description:
                                    'Videa, mapy a příspěvky ze sociálních sítí vložené přímo do stránky. Tyto služby si mohou ukládat vlastní cookies.',
                                linkedCategory: 'social',
                            },
                            {
                                title: 'Další informace',
                                description:
                                    'Co přesně ukládáme a jak dlouho, najdete v <a href="/prohlaseni-o-cookies">prohlášení o cookies</a> a v <a href="/ochrana-osobnich-udaju">zásadách ochrany osobních údajů</a>. Souhlas můžete kdykoli odvolat.',
                            },
                        ],
                    },
                },
            },
        },
    });
}

document.addEventListener('DOMContentLoaded', initCookieConsent);
